import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import {useIntl} from '@pinecast/i18n';
import styled, {useColors, useCSS} from '@pinecast/styles';
import * as React from 'react';

import type {XAlign, YAlign} from './floating/placement';
import {
  alignmentFromPlacement,
  placementFromPreferences,
} from './floating/placement';
import {ErrorIcon, Info} from './icons';

type Size = 'normal' | 'large';

const maxWidths = {
  normal: 300,
  large: 500,
};

const TRANSITION_MS = 200;

// Matches the z-index `Layer` gives its portal, so tooltips keep stacking above
// page chrome now that they portal into a plain container of their own.
const LAYER_Z_INDEX = 100;

const TooltipWrapper = styled(
  'aside',
  ({
    $size,
    $xAlign,
    $yAlign,
  }: {
    $size: Size;
    $xAlign: XAlign;
    $yAlign: YAlign;
  }) => ({
    color: '#fff',
    display: 'inline-flex',
    maxWidth: maxWidths[$size],
    // The body is hoverable even though the positioning container around it is
    // not: WCAG 2.1 SC 1.4.13 requires that moving the pointer onto tooltip
    // content not dismiss it.
    pointerEvents: 'auto',
    position: 'relative',
    transformOrigin: `${$xAlign} ${$yAlign === 'top' ? 'bottom' : 'top'}`,
    zIndex: 3,

    [$yAlign === 'bottom' ? 'paddingTop' : 'paddingBottom']: 8,

    '::after': {
      border: '0 solid rgba(0, 0, 0, 0.8)',
      content: '""',
      display: 'block',
      position: 'absolute',
      zIndex: 2,

      [$xAlign === 'left' ? 'left' : 'right']: 0,
      [$yAlign === 'bottom' ? 'top' : 'bottom']: 2,
      [$yAlign === 'bottom' ? 'borderBottom' : 'borderTop']:
        '6px solid rgba(0, 0, 0, 0.8)',
      [$xAlign === 'left' ? 'borderRight' : 'borderLeft']:
        '12px solid transparent',
    },
  }),
);

const defaultRenderWrapper = (props: React.HTMLAttributes<any>) => (
  <span {...props} style={{display: 'inline-flex', ...props.style}} />
);

type Props = {
  active?: boolean;
  children?: React.ReactNode;
  positionerStyle?: React.CSSProperties;
  preferX?: XAlign;
  preferY?: YAlign;
  renderWrapper?: (props: Record<string, any>) => React.ReactElement | null;
  size?: Size;
  style?: React.CSSProperties;
  // Whether the wrapper is itself a tab stop. It has to be for the tooltip to
  // be reachable without a pointer (WCAG 2.1 SC 1.4.13, SC 2.1.1), so it
  // defaults to one. Pass `-1` when the wrapped content is already focusable —
  // a link or an enabled button — to avoid a duplicate tab stop; `onFocus`
  // bubbles up from the child, so the tooltip still opens on focus.
  tabIndex?: number;
  tooltipContent: React.ReactNode;
  type?: 'label' | 'aux';
  offsetSkidding?: number;
  offsetDistance?: number;
};

const TooltipContainer = ({
  active = true,
  children,
  positionerStyle,
  preferX,
  preferY,
  renderWrapper,
  size = 'normal',
  style,
  tabIndex = 0,
  tooltipContent,
  type = 'aux',
  offsetSkidding,
  offsetDistance,
}: Props) => {
  const css = useCSS();

  const [open, setOpen] = React.useState(false);

  const {context, floatingStyles, placement, refs} = useFloating({
    open: open && active,
    onOpenChange: setOpen,
    placement: placementFromPreferences(preferX ?? 'left', preferY ?? 'bottom'),
    middleware: [
      // `alignmentAxis` reproduces the sign flip the old Popper `offset`
      // callback did by hand for `-end` placements.
      offset({
        mainAxis: offsetDistance ?? 0,
        alignmentAxis: offsetSkidding ?? 0,
      }),
      flip(),
      shift({padding: 8}),
    ],
    // Only attached while the tooltip is mounted, so the ResizeObserver and
    // scroll listeners go away when it closes.
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    enabled: active,
    move: false,
    // Lets the pointer travel from the anchor onto the tooltip without it
    // vanishing en route.
    handleClose: safePolygon(),
  });
  const focus = useFocus(context, {enabled: active});
  // Mouse is left to `useHover`; this covers touch taps and keyboard
  // activation, which is the only way to reach the content on a phone.
  const click = useClick(context, {
    enabled: active,
    ignoreMouse: true,
    toggle: false,
  });
  const dismiss = useDismiss(context, {enabled: active});
  const role = useRole(context, {
    enabled: active,
    role: type === 'label' ? 'label' : 'tooltip',
  });

  const {getFloatingProps, getReferenceProps} = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ]);

  // `open` is spelled out rather than left to default, which would return `''`
  // for each `initial` key and lean on empty inline styles to fall back to the
  // class.
  const {isMounted, styles: transitionStyles} = useTransitionStyles(context, {
    duration: TRANSITION_MS,
    initial: {opacity: 0, transform: 'scale(0.9)'},
    open: {opacity: 1, transform: 'scale(1)'},
  });

  const {xAlign, yAlign} = alignmentFromPlacement(placement);

  return (
    <>
      {(renderWrapper || defaultRenderWrapper)({
        ...getReferenceProps(),
        style,
        tabIndex,
        children: (
          <span
            ref={refs.setReference}
            style={{
              display: 'inline-flex',
              verticalAlign: 'middle',
              ...positionerStyle,
            }}
          >
            {children}
          </span>
        ),
      })}
      {isMounted && (
        <FloatingPortal>
          <div
            className={css({
              display: 'flex',
              justifyContent: xAlign === 'left' ? 'flex-start' : 'flex-end',
              // The container is as wide as the widest the tooltip may get, so
              // it must not swallow pointer events aimed at the page beneath.
              pointerEvents: 'none',
              width: `${maxWidths[size]}px`,
              zIndex: LAYER_Z_INDEX,
            })}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <TooltipWrapper
              $size={size}
              $xAlign={xAlign}
              $yAlign={yAlign}
              __forceStyle={transitionStyles}
            >
              <div
                className={css({
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  borderBottomLeftRadius: '3px',
                  borderBottomRightRadius: '3px',
                  borderTopLeftRadius: '3px',
                  borderTopRightRadius: '3px',
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: 0,
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  paddingRight: '8px',
                  paddingLeft: '8px',
                  zIndex: 2,
                  [`border${yAlign === 'bottom' ? 'Top' : 'Bottom'}${
                    xAlign === 'left' ? 'Left' : 'Right'
                  }Radius`]: 0,
                })}
              >
                {tooltipContent}
              </div>
            </TooltipWrapper>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default TooltipContainer;

/**
 * Wraps an icon whose tooltip *is* its content, so the icon needs both a name
 * of its own for when the tooltip is closed and button semantics for the
 * keyboard activation `useClick` provides.
 */
const useIconTooltipWrapper = (label: string) =>
  React.useCallback(
    (wrapperProps: Record<string, any>) => (
      <span
        {...wrapperProps}
        aria-label={label}
        role="button"
        style={{display: 'inline-flex', ...wrapperProps.style}}
      />
    ),
    [label],
  );

export const HelpIcon = (props: {
  children: React.ReactNode;
  iconSize?: number;
  size?: Size;
  style?: React.CSSProperties;
}) => {
  const designColors = useColors();
  const {formatMessage} = useIntl();
  const renderWrapper = useIconTooltipWrapper(
    formatMessage({
      description: 'Accessibility label for an icon that reveals help text',
      defaultMessage: 'More information',
    }),
  );
  return (
    <TooltipContainer
      renderWrapper={renderWrapper}
      size={props.size}
      style={props.style}
      tooltipContent={props.children}
      type="label"
      offsetDistance={8}
    >
      <Info color={designColors.headingBlue} size={props.iconSize || 14} />
    </TooltipContainer>
  );
};

export const WarningIcon = (props: {
  children: React.ReactNode;
  iconSize?: number;
  size?: Size;
  style?: React.CSSProperties;
}) => {
  const designColors = useColors();
  const {formatMessage} = useIntl();
  const renderWrapper = useIconTooltipWrapper(
    formatMessage({
      description: 'Accessibility label for an icon that reveals a warning',
      defaultMessage: 'Warning',
    }),
  );
  return (
    <TooltipContainer
      renderWrapper={renderWrapper}
      size={props.size}
      style={props.style}
      tooltipContent={props.children}
      type="label"
      offsetDistance={8}
    >
      <ErrorIcon
        color={designColors.borderRedAccent}
        size={props.iconSize || 14}
      />
    </TooltipContainer>
  );
};
